import {
  activeModelState,
  activeInputBoxModelState,
  setActiveModel,
  setActiveInputBoxModel,
  useAppDispatch,
  useAppSelector,
} from 'src/store';
import { useCallback, useMemo } from 'react';
import { useCurrentConversation, useGetUser } from 'src/hooks';
import { useUpdateConversationByIdMutation } from 'src/store/services';
import { ConversationUpdateRequest } from 'src/types';
import log from 'src/utils/logger';

export const useActiveModel = () => {
  const dispatch = useAppDispatch();
  const { activeModel } = useAppSelector(activeModelState);
  const { activeInputBoxModel } = useAppSelector(activeInputBoxModelState);

  const { currentConversation } = useCurrentConversation();

  const { user_id } = useGetUser();

  const [updateConversationById] = useUpdateConversationByIdMutation();

  const updateConversation = useCallback(
    async (conversation: Omit<ConversationUpdateRequest, 'user_id'>) => {
      try {
        await updateConversationById({ user_id, ...conversation }).unwrap();
      } catch (error) {
        log.error(error);
      }
    },
    [updateConversationById, user_id],
  );

  const changeInputBoxActiveModel = useCallback(
    (value?: string) => {
      dispatch(setActiveInputBoxModel(value));
    },
    [dispatch],
  );

  const changeActiveModel = useCallback(
    (value?: string) => {
      dispatch(setActiveModel(value));
      changeInputBoxActiveModel();
    },
    [dispatch, changeInputBoxActiveModel],
  );

  const changeCurrentModel = useCallback(
    (value?: string) => {
      changeActiveModel(value);
      if (!!currentConversation) {
        updateConversation({
          conversation_id: currentConversation?.conversation_id || '',
          model_ids: value ? [value] : undefined,
        });
      }
    },
    [currentConversation, changeActiveModel, updateConversation],
  );

  return useMemo(
    () => ({
      activeModel,
      activeInputBoxModel,
      changeActiveModel,
      changeInputBoxActiveModel,
      changeCurrentModel,
    }),
    [
      changeCurrentModel,
      activeModel,
      activeInputBoxModel,
      changeActiveModel,
      changeInputBoxActiveModel,
    ],
  );
};
